<template>
    <div class="option-set form-group">
        <SquareBtn v-if="showClearButton"
                   size="sm"
                   class="option-set__clear-btn"
                   @click="$emit('update:modelValue', [])">
            {{ l('CLEAR') }}
        </SquareBtn>

        <label class="control-label">
            {{ optionSet.name }}
            <span v-if="optionSet.required"
                  class="required-label">{{ l('REQUIRED') }}</span>
        </label>

        <!-- Options for this option set -->
        <!-- Rendered as radios or checkboxes depending on max choices -->
        <div class="form-group__checkboxes">
            <label v-for="itemOption in optionSet.options"
                   :key="itemOption.ID"
                   class="form-group__checkbox no-wrap clickable">
                <input :checked="modelValue && modelValue.includes(itemOption.ID)"
                       :type="inputType"
                       class="form-control field-instance-input"
                       :name="`option-set-${optionSet.ID}`"
                       :value="itemOption.ID"
                       :required="optionSet.required"
                       @change="handleRadioChange(itemOption.ID, $event)" />
                {{ itemOption.name }}
                <FieldOptionPrice :price="itemOption.price" />
            </label>
        </div>

        <div v-if="helpText"
             class="help-block">
            {{ helpText }}
        </div>
    </div>
</template>

<script>
import FieldOptionPrice from '@/vue/components/CustomFields/FieldOptionPrice';
import SquareBtn from '@/vue/components/SquareBtn';
import { container } from '@/container';
import { getHelpTextForOptionSet } from '@/lib/ItemOptions/itemOptionValidation';

export default {
    components: {
        SquareBtn,
        FieldOptionPrice,
    },

    props: {
        optionSet: {
            type: Object,
            required: true,
        },

        modelValue: {
            type: Array,
            default: () => [],
        },
    },

    emits: ['update:modelValue'],

    computed: {
        inputType() {
            return this.optionSet.maxOptions === 1 ? 'radio' : 'checkbox';
        },

        showClearButton() {
            return this.modelValue && this.modelValue.length > 0;
        },

        helpText() {
            const langString = getHelpTextForOptionSet(this.optionSet);
            return langString ? (container.langService.getText(langString) + '.') : '';
        },
    },

    methods: {
        /**
         * We need to manually handle the radio/checkbox changes so that we can have consistency
         * between radios and checkboxes.
         * If we just use v-model on the inputs, radios will set the value to a single int, and checkboxes
         * will set it to an array of ints. We want both to be arrays of ints for consistency.
         *
         * @param {number} optionID
         * @param {InputEvent} event
         */
        handleRadioChange(optionID, event) {
            let newValues = [...this.modelValue];

            if (this.inputType === 'radio') {
                // If this is a radio, clear out any existing values when changing.
                newValues = [];
            }

            if (event.target.checked) {
                // For radio, replace the array with just this option
                if (!newValues.includes(optionID)) {
                    newValues.push(optionID);
                }
            } else {
                // Remove the option from the array
                newValues = newValues.filter((id) => id !== parseInt(optionID));
            }

            newValues.sort();

            this.$emit('update:modelValue', newValues);
        },
    },
};
</script>

<style lang="less">
.option-set {
    position: relative;

    &__clear-btn {
        position: absolute;
        top: 10px;
        right: 10px;
    }
}
</style>
